import { useEffect, useRef } from "react";
import type { Account } from "../lib/types";
import { LogoutButton } from "./LogoutButton";
import "./therapist-header.css";

const icons = {
  people: <><circle cx="9" cy="7" r="3" /><path d="M3 21v-2a6 6 0 0 1 12 0v2M16 4a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 5v2" /></>,
  folder: <path d="M3 20V5h7l2 3h9v12H3Z" />,
  analysis: <><path d="M4 3v17h17M8 15v-4M13 15V7M18 15v-6" /></>,
  cards: <><rect x="7" y="7" width="14" height="14" rx="2" /><path d="M17 7V3H3v14h4M11 12h6M11 16h4" /></>,
  exercise: <><path d="M12 3H4v18h16v-8M14 5l4 4M10 17l4-1 7-7a2.8 2.8 0 0 0-4-4l-7 7v5Z" /></>,
  printer: <><path d="M7 8V3h10v5M7 17H3V8h18v9h-4M7 14h10v7H7zM17 11h1" /></>,
  board: <><rect x="3" y="3" width="18" height="13" rx="2" /><path d="m7 21 5-5 5 5M7 12l3-4 3 3 4-5" /></>,
  user: <><circle cx="12" cy="7" r="4" /><path d="M4 21v-2a8 8 0 0 1 16 0v2" /></>,
  chevron: <path d="m6 9 6 6 6-6" />,
};

function HeaderIcon({ name }: { name: keyof typeof icons }) {
  return <svg className="therapist-header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{icons[name]}</svg>;
}

interface TherapistHeaderActionsProps {
  account: Account;
  roleText: string;
  activeRoute: string;
  actingOptions: Array<{ id: string; displayName: string }>;
  canUseManagement: boolean;
  canUseIndividualExercises: boolean;
  canUseWorksheets: boolean;
  canUseWordStore?: boolean;
  wordStoreCount?: number;
  canUseQualitativeAnalysis: boolean;
  syncActive: boolean;
  onNavigate: (target: string) => void;
  onChangeActingAs: (learnerId: string) => void;
  onLogout: () => void;
}

export function TherapistHeaderActions({ account, roleText, activeRoute, actingOptions, canUseManagement, canUseIndividualExercises, canUseWorksheets, canUseWordStore = false, wordStoreCount = 0, canUseQualitativeAnalysis, syncActive, onNavigate, onChangeActingAs, onLogout }: TherapistHeaderActionsProps) {
  const root = useRef<HTMLDivElement>(null);
  const materialActive = ["flashcards", "individual-exercises", "worksheets", "whiteboard", "word-store"].includes(activeRoute);
  const selectedName = account.actingAs?.displayName ?? "Eigenes Konto";

  function closeMenus() {
    root.current?.querySelectorAll<HTMLDetailsElement>("details[open]").forEach((menu) => { menu.open = false; });
  }

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      root.current?.querySelectorAll<HTMLDetailsElement>("details[open]").forEach((menu) => { if (!menu.contains(event.target as Node)) menu.open = false; });
    };
    const closeEscape = (event: KeyboardEvent) => {
      const menu = root.current?.querySelector<HTMLDetailsElement>("details[open]");
      if (event.key !== "Escape" || !menu) return;
      event.preventDefault();
      menu.open = false;
      menu.querySelector("summary")?.focus();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, []);

  useEffect(closeMenus, [activeRoute, account.actingAs?.id]);

  function navigate(target: string) {
    const trigger = root.current?.querySelector<HTMLDetailsElement>("details[open]")?.querySelector("summary");
    closeMenus();
    trigger?.focus();
    onNavigate(target);
  }

  function item(target: string, label: string, icon: keyof typeof icons, disabled = false) {
    return <button className={`header-menu-item${activeRoute === target ? " active" : ""}`} aria-current={activeRoute === target ? "page" : undefined} aria-describedby={disabled ? "header-whiteboard-sync-hint" : undefined} disabled={disabled} onClick={() => navigate(target)}><HeaderIcon name={icon} /><span>{label}</span></button>;
  }

  return <div className="header-actions therapist-header-actions" ref={root} onClick={(event) => {
    const summary = (event.target as Element).closest("summary");
    if (!summary) return;
    root.current?.querySelectorAll<HTMLDetailsElement>("details[open]").forEach((menu) => { if (menu !== summary.parentElement) menu.open = false; });
  }}>
    <nav className="therapist-header-navigation" aria-label="Therapiebereiche">
      {canUseManagement && <button className={`header-link therapist-direct-link${activeRoute === "management" ? " active" : ""}`} aria-label="Verwaltung" title="Verwaltung" aria-current={activeRoute === "management" ? "page" : undefined} onClick={() => navigate("management")}><HeaderIcon name="people" /><span>Verwaltung</span></button>}
      {(account.modules.flashcards || canUseIndividualExercises || canUseWorksheets || canUseWordStore) && <details className={`therapist-header-menu material-header-menu${materialActive ? " active" : ""}`}>
        <summary className="header-link" aria-label="Material & Werkzeuge" title="Material & Werkzeuge"><HeaderIcon name="folder" /><span className="header-material-label">Material & Werkzeuge</span><span className="header-material-short-label">Material</span><HeaderIcon name="chevron" /></summary>
        <div className="therapist-header-panel" role="group" aria-label="Material & Werkzeuge">
          {account.modules.flashcards && item("flashcards", "Karteikarten", "cards")}
          {canUseWordStore && item("word-store", `Wortspeicher${wordStoreCount ? ` (${wordStoreCount})` : ""}`, "cards")}
          {canUseIndividualExercises && item("individual-exercises", "Meine Übungen", "exercise")}
          {canUseWorksheets && item("worksheets", "Arbeitsblätter", "printer")}
          {canUseWorksheets && <div>{item("whiteboard", "Whiteboard", "board", syncActive)}{syncActive && <p className="header-menu-hint" id="header-whiteboard-sync-hint">Bitte die Sync-Sitzung zuerst regulär beenden.</p>}</div>}
        </div>
      </details>}
      {canUseQualitativeAnalysis && <button className={`header-link therapist-direct-link${activeRoute === "analysis" ? " active" : ""}`} aria-label="Analyse" title="Analyse" aria-current={activeRoute === "analysis" ? "page" : undefined} onClick={() => navigate("analysis")}><HeaderIcon name="analysis" /><span>Analyse</span></button>}
    </nav>
    {actingOptions.length > 0 && <label className="acting-as-control"><span>Bearbeiten als</span><select value={account.actingAs?.id ?? ""} title={selectedName} onChange={(event) => onChangeActingAs(event.target.value)}><option value="">Eigenes Konto</option>{actingOptions.map((learner) => <option value={learner.id} key={learner.id}>{learner.displayName}</option>)}</select></label>}
    <details className={`therapist-header-menu profile-header-menu${activeRoute === "account" ? " active" : ""}`}>
      <summary className="header-link profile-header-trigger" aria-label={`Profil für ${account.displayName}`} title={`Profil für ${account.displayName}`}><span className="avatar" aria-hidden="true">{account.displayName.slice(0, 1).toUpperCase()}</span><HeaderIcon name="chevron" /></summary>
      <div className="therapist-header-panel" role="group" aria-label="Profil">
        <div className="header-profile-summary"><strong>{account.displayName}</strong><small>{roleText}</small></div>
        {item("account", "Mein Konto", "user")}
        <LogoutButton onLogout={onLogout} withLabel onOpen={closeMenus} />
      </div>
    </details>
  </div>;
}
